/**
 * Hook for real-time field validation
 */

import { useState, useEffect, useCallback } from 'react';
import { VulFormApiClient, FormField, RealtimeValidator } from '@vulform/core';

interface UseFieldValidationProps {
  field: FormField;
  value: any;
  apiClient?: VulFormApiClient;
  debounceMs?: number;
  validateOnChange?: boolean;
}

interface UseFieldValidationReturn {
  error: string | null;
  isValidating: boolean;
  validate: () => Promise<void>;
  clearError: () => void;
}

export function useFieldValidation(props: UseFieldValidationProps): UseFieldValidationReturn {
  const {
    field,
    value,
    apiClient,
    debounceMs = 300,
    validateOnChange = true,
  } = props;

  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validator] = useState(() => new RealtimeValidator());

  // Register field validator
  useEffect(() => {
    if (!apiClient || !field.validation) return;

    validator.registerField(field.name, async (value) => {
      return apiClient.validateField(field.name, value, field.validation!);
    });

    return () => {
      validator.destroy();
    };
  }, [field, apiClient, validator]);

  // Validate field
  const validate = useCallback(async () => {
    if (!field.validation) return;

    setIsValidating(true);
    
    try {
      await validator.validateField(
        field.name,
        value,
        (error) => {
          setError(error);
          setIsValidating(false);
        },
        debounceMs
      );
    } catch (err) {
      setIsValidating(false);
    }
  }, [field, value, validator, debounceMs]);

  // Auto-validate on value change
  useEffect(() => {
    if (validateOnChange && value !== undefined) {
      validate();
    }
  }, [value, validate, validateOnChange]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      validator.destroy();
    };
  }, [validator]);

  return {
    error,
    isValidating,
    validate,
    clearError,
  };
}

export default useFieldValidation;